/* eslint-disable react/prop-types */
import * as React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { dashboardAccessManager } from "../utils/dashBoardAccess";
import { SSOCallback, SSO_CALLBACK_PATH } from "../app/modules/Login/SSOCallback";
import { Lazy } from "./lazyModules";
import LazyRouteBoundary from "./components/LazyRouteBoundary";
import AuthenticatedLayout from "./AuthenticatedLayout";
import LazyPublicRoute from "./LazyPublicRoute";
import { isLoggedIn } from "../utils/session";
import { usePagesSetup } from "./shared/usePagesSetup";

const LazyPages = (props) => {
  const { withScreenAccess, shouldRenderReceiptsRoute, navigate } = usePagesSetup(props);

  const CustomDashboard = () => {
    if (dashboardAccessManager("bookings")) {
      return <Lazy.BookingDashboard />;
    }
    if (dashboardAccessManager("renewals")) {
      navigate("/renewal/dashboard");
      return null;
    }
    if (dashboardAccessManager("maintenance")) {
      navigate("/maintenance/dashboard");
      return null;
    }
    return <Lazy.BookingDashboard />;
  };

  return (
    <LazyRouteBoundary>
      <Routes>
        {/* Eager: SSO callback only */}
        <Route path={SSO_CALLBACK_PATH} element={<SSOCallback />} />
        <Route path="/notice" element={<LazyPublicRoute component={Lazy.ShowNotice} />} />
        <Route path="/unauthorized" element={<LazyPublicRoute component={Lazy.Unauthorized} />} />
        <Route
          path="payments/process/:leaseId/:userId"
          element={<LazyPublicRoute component={Lazy.PaymentProcess} />}
        />

        {/* Lazy: auth & onboarding */}
        <Route path="/" element={<LazyPublicRoute component={Lazy.Login} />} />
        <Route path="/login" element={<LazyPublicRoute component={Lazy.Login} />} />
        <Route path="/register" element={<LazyPublicRoute component={Lazy.SignUp} />} />
        <Route path="/signup" element={<LazyPublicRoute component={Lazy.SignUpNew} />} />
        <Route path="/otp" element={<LazyPublicRoute component={Lazy.OTP} />} />
        <Route path="/create-password" element={<LazyPublicRoute component={Lazy.CreatePassword} />} />
        <Route path="/succcreateess" element={<LazyPublicRoute component={Lazy.Success} />} />
        <Route path="/forgot-password" element={<LazyPublicRoute component={Lazy.ForgotPassword} />} />
        <Route path="/change-password" element={<LazyPublicRoute component={Lazy.ChangePassword} />} />

        {isLoggedIn() ? (
          <>
            {/* Own shell — excluded from persistent layout */}
            <Route path="/offer-by-tenant/:id" element={<LazyPublicRoute component={Lazy.OfferByTenant} />} />
            <Route path="/maintenance/:id" element={<LazyPublicRoute component={Lazy.Maintenance} />} />
            <Route path="/maintenance" element={<LazyPublicRoute component={Lazy.Maintenance} />} />
            <Route path="/create-a-property" element={<LazyPublicRoute component={Lazy.CreateProperty} />} />

            <Route element={<AuthenticatedLayout />}>
              <Route path="/dashboard" element={<CustomDashboard />} />
              <Route
                path="/maintenance/dashboard"
                element={withScreenAccess(Lazy.MaintenanceDashboard, "maintenance", true)}
              />
              <Route path="renewal/dashboard" element={withScreenAccess(Lazy.RenewalDashboard, "renewals", true)} />

              <Route path="/properties" element={withScreenAccess(Lazy.PropertyList, "properties")} />
              <Route path="/reviews" element={<Lazy.Reviews />} />
              <Route path="/property-review" element={<Lazy.PropertyReview />} />
              <Route path="/leads" element={<Lazy.Leads />} />
              <Route path="/lead-detail/:id" element={<Lazy.LeadsDetail />} />
              <Route path="/property-detail/:id" element={<Lazy.PropertyDetail />} />
              <Route path="/property-detail/:id/unit/:id" element={<Lazy.UnitDetail />} />
              <Route path="/visit-scheduling/:id" element={<Lazy.VisitScheduling />} />
              <Route path="/visit-scheduling" element={<Lazy.VisitScheduling />} />
              <Route path="/properties-map" element={<Lazy.PropertiesOnMap />} />
              <Route path="/opportunities" element={<Lazy.OpportuntiyList />} />
              <Route path="/opportunity-detail/:id/:assetId/:partyId" element={<Lazy.OpportunityDetail />} />
              <Route path="/units" element={<Lazy.Units />} />

              <Route path="/tenants/:id" element={<Lazy.Tenants />} />
              <Route path="/tenants" element={withScreenAccess(Lazy.Tenants, "tenants")} />
              <Route path="/managers/:id" element={<Lazy.Managers />} />
              <Route path="/users" element={withScreenAccess(Lazy.Managers, "users")} />
              <Route path="/owners" element={<Lazy.Owner />} />
              <Route path="/add-tenant" element={<Lazy.AddTenant />} />
              <Route path="/tenants-detail/:id" element={<Lazy.TenantDetails />} />
              <Route path="/detail-tenant/:id/:partyId" element={<Lazy.ContractTenantDetails />} />
              <Route path="/prospects" element={<Lazy.AllProspects />} />
              <Route path="/prospect-detail/:id" element={<Lazy.TenantDetails />} />

              <Route path="/booking-form/:id" element={<Lazy.BookingForm />} />
              <Route path="/bookings/form" element={<Lazy.BookingForm />} />
              <Route path="/bookings/form/:LEASEID" element={<Lazy.BookingForm />} />
              <Route path="/renewals/:leaseId" element={<Lazy.CommercialRenewalTerms />} />
              <Route path="/renewals/form/:LEASEID" element={<Lazy.RenewalForm />} />
              <Route path="/bookingforms" element={withScreenAccess(Lazy.BookingformList, "leases")} />
              <Route path="/newbookingform" element={<Lazy.NewBookingForm />} />
              <Route path="/newbookingform/:leaseId" element={<Lazy.NewBookingForm />} />
              <Route path="/viewbookingform/:leaseId/*" element={<Lazy.ViewBookingForm />} />
              <Route path="/leads/:leadId/booking-form/:assetId/:partyId" element={<Lazy.ContractBookingForm />} />
              <Route path="/bookings" element={withScreenAccess(Lazy.BookingListing, "bookings")} />
              <Route
                path="/booking-Renewals"
                element={withScreenAccess(Lazy.BookingRenewalListing, "renewalsInProgress")}
              />

              <Route path="/contracts" element={withScreenAccess(Lazy.ContractsList, "upcomingRenewals")} />
              <Route path="/contract-detail/:id" element={<Lazy.ContractDetail />} />
              <Route path="/invoicing" element={withScreenAccess(Lazy.PostDatedCheques, "payments")} />
              {shouldRenderReceiptsRoute && <Route path="/receipts" element={<Lazy.Receipts />} />}
              <Route path="/ejari" element={<Lazy.EjariList />} />
              <Route path="/move-outs" element={withScreenAccess(Lazy.MoveOut, "moveouts")} />
              <Route path="/move-out/summery/:leaseId" element={<Lazy.TerminationProcess />} />
              <Route path="/cheques/:id" element={<Lazy.ChequesApproval />} />

              <Route path="/service-providers" element={withScreenAccess(Lazy.ServiceProvider, "serviceProviders")} />
              <Route path="/tickets" element={withScreenAccess(Lazy.Tickets, "tickets")} />
              <Route path="/technicians" element={<Lazy.Technicians />} />
              <Route path="/roster" element={<Lazy.Roster />} />
              <Route path="/zones" element={<Lazy.Zones />} />
              <Route path="/service-requests" element={<Lazy.ServiceRequests />} />

              <Route path="/configurations" element={<Lazy.Configurations />} />
              <Route path="/open-hours" element={<Lazy.Shifts />} />
              <Route path="/categories" element={<Lazy.Categories />} />
              <Route path="/notices" element={withScreenAccess(Lazy.Notices, "notices")} />
              <Route path="/public-holidays" element={<Lazy.PublicHolidays />} />
              <Route path="/notifications" element={<Lazy.Notification />} />
              <Route path="/notifications/calendar" element={<Lazy.NotificationCalendar />} />
              <Route path="/contactus" element={<Lazy.ContactUs />} />

              <Route path="*" element={<Lazy.NotFound />} />
            </Route>
          </>
        ) : (
          <Route path="*" element={<Navigate to="/login" replace />} />
        )}
      </Routes>
    </LazyRouteBoundary>
  );
};

export default LazyPages;
